/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  isAddNewLteX2ConnectionSelector,
  lteX2ConnectionListSelector,
  lteX2ConnectionIndexesSelector,
} from "../../../../store/api/deviceConfig/selectors";
import { IltepeerInterfaces } from "../../../../store/api/deviceConfig/types";
import { getNextLteX2ConnectionIndex, prepareLteX2ConnectionPayload } from "../../helpers";
import { selectedDeviceSelector } from "../../../../store/api/devices/selectors";
import { OPERATIONTYPES } from "../../../../globals/constants";
import { useSetDeviceConfigMutation } from "../../../../store/api/deviceConfig/deviceConfigApiSlice";
import { useStoreDispatch } from "../../../../store/hooks";
import { setOpenCustomDailog, setOpenToast } from "../../../../store/helpers/helperReducers";
import { setLteX2ConnectionList } from "../../../../store/api/deviceConfig/reducers";
import { initialState } from "../../../../store/api/deviceConfig/initialState";
import { errorMessage } from "../../../../utils/helpers";
import { isIPv6, isIPv4 } from "../../../../utils/validations";

interface IPayload {
  [key: string]: string;
}

export const useLteX2ConnectionData = (selectedLteX2Connection?: IltepeerInterfaces) => {
  const dispatch = useStoreDispatch();
  const [lteX2ConnectionData, setLteX2ConnectionData] = useState(selectedLteX2Connection);
  const [errMsg, setErrMsg] = useState<IPayload>({});
  const [anchorElLteX2Connection, setAnchorElLteX2Connection] = useState<null | HTMLElement>(null);
  const [setDeviceConfig, configStatus] = useSetDeviceConfigMutation();

  let lteX2ConnectionList: IltepeerInterfaces[] = useSelector(lteX2ConnectionListSelector);
  const isAddNewLteX2Connection = useSelector(isAddNewLteX2ConnectionSelector);
  const selectedDevice = useSelector(selectedDeviceSelector);
  const lteX2ConnectionIndexes = useSelector(lteX2ConnectionIndexesSelector);
  const nextLteX2ConnectionIndex = getNextLteX2ConnectionIndex(lteX2ConnectionIndexes, 10);

  useEffect(() => {
    if (isAddNewLteX2Connection) {
      setLteX2ConnectionData({
        ...initialState.selectedLteX2Connection,
        index: nextLteX2ConnectionIndex!,
      });
    } else {
      setLteX2ConnectionData(selectedLteX2Connection);
    }
  }, [isAddNewLteX2Connection, nextLteX2ConnectionIndex, selectedLteX2Connection]);

  const onblureHandler = (event: any) => {
    const { name, value } = event.target;
    if (name === "index") {
      if (lteX2ConnectionIndexes?.includes(lteX2ConnectionData?.index)) {
        setErrMsg({ ...errMsg, index: `Index ${lteX2ConnectionData?.index} is already in use.` });
        return;
      }
    }
    if (name === "address" && !!value) {
      const isValid = isIPv4(value) || isIPv6(value);
      !isValid && setErrMsg({ ...errMsg, [name]: `Invalid IP Address` });
    }

    if (nextLteX2ConnectionIndex !== lteX2ConnectionData?.index) {
      setLteX2ConnectionData({ ...lteX2ConnectionData! });
    }
  };

  const submitHandler = async () => {
    if (!!lteX2ConnectionData) {
      if (Object.keys(lteX2ConnectionData).length <= 0) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: "Please fill the required fields!" }));
        return;
      }

      const operationType = isAddNewLteX2Connection ? OPERATIONTYPES.CREATE : OPERATIONTYPES.UPDATE;
      const LteX2ConnectionPayload =
        !!lteX2ConnectionData &&
        !!selectedDevice?.objectKey &&
        prepareLteX2ConnectionPayload(lteX2ConnectionData, selectedLteX2Connection, selectedDevice?.objectKey, operationType);
      if (!!lteX2ConnectionData) {
        lteX2ConnectionList = [lteX2ConnectionData, ...lteX2ConnectionList];
      }
      try {
        let result: any;
        dispatch(setLteX2ConnectionList(lteX2ConnectionList));
        dispatch(setOpenCustomDailog(false));
        setAnchorElLteX2Connection(null);

        result = await setDeviceConfig(LteX2ConnectionPayload);
        if (result?.error || configStatus?.isError) throw result?.error;
        if (result.data?.status === 200) {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: `The data has been submited`,
            })
          );
        }
      } catch (error: any) {
        let lastAdded = lteX2ConnectionList.filter((item) => item.index === lteX2ConnectionData.index)[0];
        lastAdded = { ...lastAdded };
        const updateToLteX2ConnectionList = lteX2ConnectionList.filter((item) => item.index !== lteX2ConnectionData.index);
        dispatch(setLteX2ConnectionList([lastAdded, ...updateToLteX2ConnectionList]));
        setTimeout(() => {
          const removedFailed = lteX2ConnectionList.filter((item) => item.index !== lteX2ConnectionData.index);
          dispatch(setLteX2ConnectionList(removedFailed));
        }, 5000);
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    }
  };

  return {
    lteX2ConnectionData,
    setLteX2ConnectionData,
    isAddNewLteX2Connection,
    submitHandler,
    onblureHandler,
    errMsg,
    setErrMsg,
    anchorElLteX2Connection,
    setAnchorElLteX2Connection,
  };
};
